import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Fetch security settings for the current workspace
export const fetchSecuritySettings = createAsyncThunk(
  "settings/fetchSecuritySettings",
  async (_, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/workspaces/security-settings`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || "Failed to fetch security settings"
        );
      }

      const data = await response.json();
      return {
        autoPasswordChangeEnabled: false,
        autoPasswordChangeIntervalDays: 10,
        ownerAutoPasswordChangeEnabled: false,
        ownerAutoPasswordChangeIntervalDays: 10,
        ...(data.securitySettings || {}),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update security settings for the current workspace
export const updateSecuritySettings = createAsyncThunk(
  "settings/updateSecuritySettings",
  async (payload, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/workspaces/security-settings`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(
          data.message || "Failed to update security settings"
        );
      }

      return data.securitySettings;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const patchRoleSecurityPolicy = createAsyncThunk(
  "settings/patchRoleSecurityPolicy",
  async ({ roleId, twoFactorRequired, includeInAutoPasswordRotation }, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }
    const body = {};
    if (typeof twoFactorRequired === "boolean") body.twoFactorRequired = twoFactorRequired;
    if (typeof includeInAutoPasswordRotation === "boolean") {
      body.includeInAutoPasswordRotation = includeInAutoPasswordRotation;
    }
    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/roles/roles/${roleId}/security-policy`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to update role security policy");
      }
      return data.role;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching team members
export const fetchTeamMembers = createAsyncThunk(
  "settings/fetchTeamMembers",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teammembers/get`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Try to extract error message from response
        let errorMessage = "Failed to fetch team members";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        // Handle specific status codes
        if (response.status === 403) {
          errorMessage = "Permission denied: You don't have access to view team members";
        } else if (response.status === 401) {
          errorMessage = "Authentication failed: Please log in again";
        } else if (response.status === 500) {
          errorMessage = "Server error: Please try again later";
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      // Handle case where server returns empty array (no team members)
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching active team members
export const fetchActiveMembers = createAsyncThunk(
  "settings/fetchActiveMembers",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teammembers/getactive`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Try to extract error message from response
        let errorMessage = "Failed to fetch active team members";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        // Handle specific status codes
        if (response.status === 403) {
          errorMessage = "Permission denied: You don't have access to view team members";
        } else if (response.status === 401) {
          errorMessage = "Authentication failed: Please log in again";
        } else if (response.status === 500) {
          errorMessage = "Server error: Please try again later";
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      // Handle case where server returns empty array (no active team members)
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting a team member
export const deleteTeamMember = createAsyncThunk(
  "settings/deleteTeamMember",
  async (_id, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teammembers/delete/${_id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to delete team member");
      }

      return _id; // Return the deleted team member's ID
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const setTeamLeader = createAsyncThunk(
  "settings/setTeamLeader",
  async ({ teamId, teamLeaderId }, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teams/${teamId}/leader`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ teamLeaderId: teamLeaderId ?? null }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to set team leader");
      }

      const data = await response.json();
      return data.team;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching teams
export const fetchTeams = createAsyncThunk(
  "settings/fetchTeams",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teams/get`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Try to extract error message from response
        let errorMessage = "Failed to fetch teams";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        // Handle specific status codes
        if (response.status === 403) {
          errorMessage = "Permission denied: You don't have access to view teams";
        } else if (response.status === 401) {
          errorMessage = "Authentication failed: Please log in again";
        } else if (response.status === 500) {
          errorMessage = "Server error: Please try again later";
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      // Handle case where server returns empty array (no teams)
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting a team
export const deleteTeam = createAsyncThunk(
  "settings/deleteTeam",
  async (_id, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teams/delete/${_id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete team");
      }

      return _id; // Return the deleted team's ID
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating a team
export const updateTeam = createAsyncThunk(
  "settings/updateTeam",
  async ({ teamId, teamData }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teams/update/${teamId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(teamData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update team");
      }

      const data = await response.json();
      return data.team; // Return the updated team
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching roles with usage
export const fetchRolesWithUsage = createAsyncThunk(
  "settings/fetchRolesWithUsage",
  async (_, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/roles/details`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch roles with usage");
      }

      const data = await response.json();
      return data; // Return roles with usage details
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting a role
export const deleteRole = createAsyncThunk(
  "settings/deleteRole",
  async (_id, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/roles/delete/${_id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete role");
      }

      return _id; // Return the deleted role's ID
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  selectedOption: "General", // Default selected option
  teamMembers: [],
  teams: [],
  rolesWithUsage: [], // Array to hold roles with usage
  selectedTeam: null, // New state for selected team
  loading: false,
  error: null,
  securitySettings: {
    autoPasswordChangeEnabled: false,
    autoPasswordChangeIntervalDays: 10,
    ownerAutoPasswordChangeEnabled: false,
    ownerAutoPasswordChangeIntervalDays: 10,
  },
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setSelectedOption: (state, action) => {
      state.selectedOption = action.payload;
    },
    setSelectedTeam: (state, action) => {
      state.selectedTeam = action.payload; // Update the selected team
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch team members
      .addCase(fetchTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.message === "Permission denied") {
          state.teamMembers = [];
        } else {
          state.teamMembers = action.payload;
          state.error = null;
        }
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.teamMembers = [];
        state.error = action.payload;
      })
      // Fetch active members
      .addCase(fetchActiveMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.activeMembers = action.payload;
        state.error = null;
      })
      .addCase(fetchActiveMembers.rejected, (state, action) => {
        state.loading = false;
        state.activeMembers = [];
        state.error = action.payload;
      })
      // Delete a team member
      .addCase(deleteTeamMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTeamMember.fulfilled, (state, action) => {
        state.loading = false;
        state.teamMembers = state.teamMembers.filter(
          (member) => member.id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteTeamMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch teams
      .addCase(fetchTeams.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.message === "Permission denied") {
          state.teams = [];
        } else {
          state.teams = action.payload;
          state.error = null;
        }
      })
      .addCase(setTeamLeader.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated?._id) return;
        const id = String(updated._id);
        state.teams = (state.teams || []).map((team) =>
          String(team._id || team.id) === id ? updated : team
        );
        if (
          state.selectedTeam &&
          String(state.selectedTeam._id || state.selectedTeam.id) === id
        ) {
          state.selectedTeam = updated;
        }
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        state.loading = false;
        state.teams = [];
        state.error = action.payload;
      })
      // Delete a team
      .addCase(deleteTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        state.loading = false;
        state.teams = state.teams.filter((team) => team.id !== action.payload);
        // Reset selectedTeam if the deleted team was selected
        if (state.selectedTeam && state.selectedTeam.id === action.payload) {
          state.selectedTeam = null;
        }
        state.error = null;
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update a team
      .addCase(updateTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        state.loading = false;
        // Update the team in the teams array
        const index = state.teams.findIndex(team => team._id === action.payload._id);
        if (index !== -1) {
          state.teams[index] = action.payload;
        }
        // Update selectedTeam if it's the one being updated
        if (state.selectedTeam && state.selectedTeam._id === action.payload._id) {
          state.selectedTeam = action.payload;
        }
        state.error = null;
      })
      .addCase(updateTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch roles with usage
      .addCase(fetchRolesWithUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRolesWithUsage.fulfilled, (state, action) => {
        state.loading = false;
        state.rolesWithUsage = action.payload;
        state.error = null;
      })
      .addCase(fetchRolesWithUsage.rejected, (state, action) => {
        state.loading = false;
        state.rolesWithUsage = [];
        state.error = action.payload;
      })
      // Delete a role
      .addCase(deleteRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.loading = false;
        state.rolesWithUsage = state.rolesWithUsage.filter(
          (role) => role.id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch security settings
      .addCase(fetchSecuritySettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSecuritySettings.fulfilled, (state, action) => {
        state.loading = false;
        state.securitySettings = action.payload;
      })
      .addCase(fetchSecuritySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update security settings
      .addCase(updateSecuritySettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSecuritySettings.fulfilled, (state, action) => {
        state.loading = false;
        state.securitySettings = action.payload;
      })
      .addCase(updateSecuritySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(patchRoleSecurityPolicy.fulfilled, (state, action) => {
        const r = action.payload;
        if (!r?._id) return;
        state.rolesWithUsage = (state.rolesWithUsage || []).map((row) =>
          row._id === r._id
            ? {
                ...row,
                twoFactorRequired: r.twoFactorRequired,
                includeInAutoPasswordRotation: r.includeInAutoPasswordRotation,
              }
            : row
        );
      });
  },
});

export const { setSelectedOption, setSelectedTeam } = settingsSlice.actions;

export default settingsSlice.reducer;
